import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { baseUniDriverFactory } from '../../unidriver';
import { CustomFieldType } from '@wix/bex-core';
import {
  CheckboxUniDriver,
  DatePickerUniDriver,
  DropdownUniDriver,
  FormFieldUniDriver,
  InputAreaUniDriver,
  InputUniDriver,
  NumberInputUniDriver,
  TimeInputUniDriver,
} from '@wix/design-system/dist/testkit/unidriver';
import { dataHooks } from '../dataHooks';

export default (base: UniDriver, body: UniDriver) => {
  const fieldType = () =>
    base.attr('data-field-type') as Promise<CustomFieldType | null | undefined>;

  const inputBase = (suffix = '') =>
    base.$(`[data-hook="${dataHooks.Input}${suffix}"]`);

  const dropdown = () => DropdownUniDriver(inputBase(), body);
  const checkbox = () => CheckboxUniDriver(inputBase(), body);

  const formField = () =>
    FormFieldUniDriver(base.$(`[data-hook="${dataHooks.Label}"]`), body);

  return {
    /**
     * Gets field label
     */
    getLabel: async () => {
      const type = await fieldType();

      if (type === 'checkbox') {
        return checkbox().getLabel();
      }

      const element = await formField().getLabel();
      return element?.textContent;
    },
    /**
     * Gets field value
     */
    getValue: async () => {
      const type = await fieldType();

      switch (type) {
        case 'shortText':
          return InputUniDriver(inputBase(), body).getValue();
        case 'date':
          return DatePickerUniDriver(inputBase(), body).inputDriver.getValue();
        case 'dateTime':
          const date = await DatePickerUniDriver(
            inputBase('-date'),
            body,
          ).inputDriver.getValue();
          const format = Intl.DateTimeFormat([], {
            hour: 'numeric',
            minute: 'numeric',
          });

          const time = await TimeInputUniDriver(
            inputBase('-time'),
            body,
          ).getValue();
          return `${date} ${format.format(time)}`;
        case 'checkbox':
          return checkbox().isChecked();
        case 'longText':
          return InputAreaUniDriver(inputBase(), body).getValue();
        case 'dropdown':
          return dropdown().inputDriver.getValue();
        case 'integer':
        case 'decimal':
          return NumberInputUniDriver(inputBase(), body).getValue();
        default:
          throw new Error(`Driver for type '${type}' not found.`);
      }
    },
    /**
     * Gets field status message
     */
    getStatusMessage: async () => {
      const type = await fieldType();

      switch (type) {
        case 'shortText':
          return InputUniDriver(inputBase(), body).getStatusMessage();
        case 'date':
          return DatePickerUniDriver(
            inputBase(),
            body,
          ).inputDriver.getStatusMessage();
        case 'dateTime':
          // internal validation message doesn't propagate
          // const date = await DatePickerUniDriver(
          //   inputBase('-date'),
          //   body,
          // ).inputDriver.getStatusMessage();

          const time = await TimeInputUniDriver(
            inputBase('-time'),
            body,
          ).getStatusMessage();
          return [time].join('\n');
        case 'longText':
          return InputAreaUniDriver(inputBase(), body).getStatusMessage();
        case 'dropdown':
          return dropdown().inputDriver.getStatusMessage();
        case 'integer':
        case 'decimal':
          return NumberInputUniDriver(inputBase(), body).getStatusMessage();
        default:
          throw new Error(`Driver for type '${type}' not found.`);
      }
    },
    /**
     * Enters field value, handles different value types based on field type
     */
    enterValue: async (
      value: any,
      { locale = 'en' }: { locale?: string } = {},
    ) => {
      const type = await fieldType();

      switch (type) {
        case 'shortText':
          return InputUniDriver(inputBase(), body).enterText(value);
        case 'date':
          return DatePickerUniDriver(inputBase(), body).inputDriver.enterText(
            typeof value === 'string'
              ? value
              : `${Intl.DateTimeFormat().format(value)}`,
          );
        case 'dateTime':
          let dateValue = '';
          let timeValue = '';

          if (typeof value === 'string') {
            const parts = value.split('T');
            dateValue = parts[0];
            timeValue = parts[1];
          } else {
            dateValue = `${Intl.DateTimeFormat([locale]).format(value)}`;
            timeValue = Intl.DateTimeFormat([locale], {
              hour: 'numeric',
              minute: 'numeric',
            }).format(value);
          }

          await DatePickerUniDriver(
            inputBase('-date'),
            body,
          ).inputDriver.enterText(dateValue);

          return TimeInputUniDriver(inputBase('-time'), body).setValue(
            timeValue,
          );
        case 'checkbox':
          const checkboxDriver = checkbox();
          const isChecked = await checkboxDriver.isChecked();
          if (value && !isChecked) {
            await checkboxDriver.click();
          } else if (isChecked && !value) {
            await checkboxDriver.click();
          }
          return;
        case 'longText':
          return InputAreaUniDriver(inputBase(), body).enterText(value);
        case 'dropdown':
          const dropdownDriver = dropdown();
          await dropdownDriver.dropdownLayoutDriver.click();
          return dropdownDriver.dropdownLayoutDriver.clickAtOptionWithValue(
            value,
          );
        case 'integer':
        case 'decimal':
          return NumberInputUniDriver(inputBase(), body).enterText(value);
        default:
          throw new Error(`Driver for type '${type}' not found.`);
      }
    },
    /**
     * Returns available dropdown options
     */
    getOptions: async () => {
      const type = await fieldType();

      if (type !== 'dropdown') {
        throw new Error(`Field type '${type}' does not support this method.`);
      }

      return dropdown().dropdownLayoutDriver.optionsContent();
    },
    /**
     * Returns remaining chars count on fields with maxLength
     */
    getLengthLeft: async () => {
      return formField().getLengthLeft();
    },
    ...baseUniDriverFactory(base),
  };
};
